import React from 'react';
import { Button } from 'primereact/button';
import type { Customer } from '../../types/customer';

/**
 * Template for rendering boolean fields as checkmark or X icon
 */
export const booleanBodyTemplate = (rowData: Customer, column: { field: string }) => {
  const isEnabled = rowData[column.field as keyof Customer];
  return (
    <div style={{ textAlign: 'center' }}>
      <i className={isEnabled ? "pi pi-check" : "pi pi-times"}></i>
    </div>
  );
};

/**
 * Template for rendering array fields as a list
 */
export const listBodyTemplate = (rowData: Customer, column: { field: string }) => {
  const fieldValue = rowData[column.field as keyof Customer];
  const items = Array.isArray(fieldValue) ? fieldValue : [];
  
  return (
    <React.Fragment>
      <ol style={{ textAlign: 'left', paddingLeft: 0, margin: 0 }}>
        {items.map((el: string) => (
          <li style={{ listStyle: "none", textAlign: 'left' }} key={el}>{el}</li>
        ))}
      </ol>
    </React.Fragment>
  );
};

/**
 * Template for rendering country with flag image
 */
export const countryBodyTemplate = (rowData: Customer) => {
  const countryCode = (rowData.country || '').trim().toLowerCase();
  
  // Map of country codes to English names
  const countryNames: Record<string, string> = {
    'fr': 'France', 'de': 'Germany', 'us': 'United States', 'gb': 'United Kingdom', 'it': 'Italy',
    'es': 'Spain', 'nl': 'Netherlands', 'be': 'Belgium', 'ch': 'Switzerland', 'at': 'Austria',
    'pl': 'Poland', 'se': 'Sweden', 'no': 'Norway', 'dk': 'Denmark', 'fi': 'Finland',
    'pt': 'Portugal', 'gr': 'Greece', 'cz': 'Czech Republic', 'hu': 'Hungary', 'ro': 'Romania',
    'ie': 'Ireland', 'sk': 'Slovakia', 'bg': 'Bulgaria', 'hr': 'Croatia', 'si': 'Slovenia',
    'lt': 'Lithuania', 'lv': 'Latvia', 'ee': 'Estonia', 'lu': 'Luxembourg', 'mt': 'Malta',
    'cy': 'Cyprus', 'ca': 'Canada', 'mx': 'Mexico', 'br': 'Brazil', 'ar': 'Argentina',
    'jp': 'Japan', 'cn': 'China', 'kr': 'South Korea', 'in': 'India', 'au': 'Australia',
    'nz': 'New Zealand', 'za': 'South Africa', 'ru': 'Russia', 'tr': 'Turkey', 'ua': 'Ukraine'
  };
  
  if (!countryCode || countryCode.length !== 2) {
    return <span>{countryCode.toUpperCase()}</span>;
  }
  
  const countryName = countryNames[countryCode] || countryCode.toUpperCase();
  const flagUrl = `https://flagcdn.com/24x18/${countryCode}.png`;
  
  return (
    <React.Fragment>
      <img 
        src={flagUrl} 
        alt={countryName}
        title={countryName}
        style={{ 
          width: '24px',
          height: '18px',
          objectFit: 'cover',
          borderRadius: '2px',
          boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
        }}
        onError={(e) => {
          // Fallback to text if image fails to load
          e.currentTarget.style.display = 'none';
          e.currentTarget.parentElement!.textContent = countryCode.toUpperCase();
        }}
      />
    </React.Fragment>
  );
};

/**
 * Template for rendering language with styled badge
 */
export const languageBodyTemplate = (rowData: Customer) => {
  const langCode = (rowData.language || '').trim().toLowerCase();
  
  // Map of language codes to English names and colors
  const languageMap: Record<string, { bg: string, text: string, name: string }> = {
    'en': { bg: '#4A90E2', text: '#fff', name: 'English' },
    'de': { bg: '#E74C3C', text: '#fff', name: 'German' },
    'fr': { bg: '#3498DB', text: '#fff', name: 'French' },
    'es': { bg: '#F39C12', text: '#fff', name: 'Spanish' },
    'it': { bg: '#27AE60', text: '#fff', name: 'Italian' },
    'nl': { bg: '#E67E22', text: '#fff', name: 'Dutch' },
    'pt': { bg: '#16A085', text: '#fff', name: 'Portuguese' },
    'pl': { bg: '#8E44AD', text: '#fff', name: 'Polish' },
    'sv': { bg: '#2980B9', text: '#fff', name: 'Swedish' },
    'da': { bg: '#C0392B', text: '#fff', name: 'Danish' },
    'no': { bg: '#D35400', text: '#fff', name: 'Norwegian' },
    'fi': { bg: '#2C3E50', text: '#fff', name: 'Finnish' },
    'cs': { bg: '#7F8C8D', text: '#fff', name: 'Czech' },
    'hu': { bg: '#95A5A6', text: '#fff', name: 'Hungarian' },
    'ro': { bg: '#34495E', text: '#fff', name: 'Romanian' },
    'sk': { bg: '#5D6D7E', text: '#fff', name: 'Slovak' },
    'bg': { bg: '#1ABC9C', text: '#fff', name: 'Bulgarian' },
    'el': { bg: '#3498DB', text: '#fff', name: 'Greek' },
    'ru': { bg: '#E74C3C', text: '#fff', name: 'Russian' },
    'tr': { bg: '#E74C3C', text: '#fff', name: 'Turkish' },
    'ja': { bg: '#E74C3C', text: '#fff', name: 'Japanese' },
    'zh': { bg: '#E74C3C', text: '#fff', name: 'Chinese' },
    'ko': { bg: '#3498DB', text: '#fff', name: 'Korean' }
  };
  
  const style = languageMap[langCode] || { bg: '#95A5A6', text: '#fff', name: langCode.toUpperCase() };
  
  return (
    <React.Fragment>
      <span 
        style={{ 
          display: 'inline-block',
          padding: '2px 6px',
          borderRadius: '3px',
          backgroundColor: style.bg,
          color: style.text,
          fontSize: '0.7rem',
          fontWeight: 'bold',
          letterSpacing: '0.5px',
          textTransform: 'uppercase'
        }} 
        title={style.name}
      >
        {langCode}
      </span>
    </React.Fragment>
  );
};

/**
 * Template for rendering action buttons (edit and delete)
 */
export const createActionBodyTemplate = (
  onEdit: (customer: Customer) => void,
  onDelete: (customer: Customer) => void
) => {
  return (rowData: Customer) => {
    return (
      <React.Fragment>
        <Button 
          icon="pi pi-pencil" 
          className="p-button-rounded p-button-outlined p-button-success mr-2" 
          onClick={() => onEdit(rowData)} 
        />
        <Button 
          icon="pi pi-trash" 
          className="p-button-rounded p-button-outlined p-button-warning" 
          onClick={() => onDelete(rowData)} 
        />
      </React.Fragment>
    );
  };
};
